/**
 * Phase 5.4a (2026-05-15) — Offline status indicator.
 *
 * Sticky banner under the main header.
 *   - Online + 0 pending  → hidden (zero noise on the happy path)
 *   - Online + N pending  → yellow "Sync en cours — N actions"
 *   - Offline             → red "Hors ligne — N actions en attente"
 *
 * Clicking the banner opens a modal listing every pending / failed
 * action with its timestamp and the latest error.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  WifiOff, CloudOff, RefreshCw, AlertTriangle, CheckCircle2, X, ChevronRight,
} from 'lucide-react';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import syncEngine from '../services/syncEngine';
import { listOutbox, deleteOutboxAction } from '../services/offlineDB';

const fmtTime = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch (_) { return ''; }
};

const Banner = ({ tone, icon: Icon, text, onClick, spinning }) => (
  <button
    onClick={onClick}
    data-testid="offline-status-banner"
    className={`w-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 border-b transition-colors ${
      tone === 'offline'
        ? 'bg-red-500/15 text-red-200 border-red-500/30 hover:bg-red-500/20'
        : 'bg-amber-500/15 text-amber-100 border-amber-500/30 hover:bg-amber-500/20'
    }`}
  >
    <Icon className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`} />
    {text}
    <ChevronRight className="w-3.5 h-3.5 opacity-60" />
  </button>
);

const OfflineStatusBar = () => {
  const { online, pending, syncing, lastError } = useOfflineStatus();
  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const refreshList = async () => {
    try {
      const all = await listOutbox();
      setActions(all);
    } catch (_) { /* noop */ }
  };

  useEffect(() => { if (open) refreshList(); }, [open]);
  useEffect(() => { if (open) { const t = setInterval(refreshList, 2000); return () => clearInterval(t); } }, [open]);

  const tone = !online ? 'offline' : 'syncing';
  const visible = !online || pending > 0 || syncing;

  const statusText = useMemo(() => {
    if (!online) return `Hors ligne — ${pending} action(s) en attente`;
    if (syncing) return `Synchronisation en cours… (${pending})`;
    if (pending > 0) return `${pending} action(s) à synchroniser`;
    return 'En ligne — Sync à jour';
  }, [online, pending, syncing]);

  if (!visible) return null;

  return (
    <>
      <Banner
        tone={tone}
        icon={!online ? WifiOff : CloudOff}
        text={statusText}
        spinning={syncing}
        onClick={() => setOpen(true)}
      />

      {open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur" data-testid="offline-actions-modal">
          <div className="w-full max-w-2xl bg-gray-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            <header className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h2 className="text-base font-semibold">Actions hors ligne</h2>
                <p className="text-xs text-gray-500">
                  {online ? `En ligne — ${pending} action(s) en attente de sync` : 'Hors ligne — sync différée'}
                  {lastError && <span className="ml-2 text-red-300">• Dernière erreur : {lastError}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => { setRefreshing(true); await syncEngine.flush(); await refreshList(); setRefreshing(false); }}
                  disabled={!online || refreshing}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-40 text-xs font-medium flex items-center gap-1.5"
                  data-testid="offline-flush-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                  Synchroniser maintenant
                </button>
                <button
                  onClick={async () => { await syncEngine.retryFailed(); await refreshList(); }}
                  className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 text-xs font-medium"
                  data-testid="offline-retry-failed-btn"
                >
                  Relancer échecs
                </button>
                <button
                  onClick={async () => {
                    // Phase 5.4a-hotfix — last-resort recovery for users
                    // stuck on a stale bundle / SW. Wipes SWs + caches and
                    // hard-reloads. Outbox actions in IndexedDB are KEPT.
                    if (!window.confirm('Recharger l\'application avec la dernière version ? Les actions en attente seront conservées.')) return;
                    try {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
                      const keys = await caches.keys();
                      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
                    } catch (_) { /* noop */ }
                    window.location.reload();
                  }}
                  className="px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-200 hover:bg-red-500/25 text-xs font-medium"
                  data-testid="offline-force-update-btn"
                  title="Forcer le rechargement avec la dernière version de l'app"
                >
                  Forcer mise à jour
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-white/5"
                  data-testid="offline-modal-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </header>

            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
              {actions.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-10" data-testid="offline-empty">
                  <CheckCircle2 className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  Aucune action en attente
                </div>
              )}
              {actions.map((a) => (
                <div
                  key={a.id}
                  className="rounded-xl bg-white/5 border border-white/10 p-3 text-xs"
                  data-testid={`offline-action-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                        a.status === 'done' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                        a.status === 'failed' ? 'bg-red-500/15 text-red-300 border-red-500/30' :
                        a.status === 'syncing' ? 'bg-sky-500/15 text-sky-300 border-sky-500/30' :
                        'bg-amber-500/15 text-amber-200 border-amber-500/30'
                      }`}>
                        {a.status}
                      </span>
                      <code className="text-gray-300 truncate">{a.method} {a.url}</code>
                    </div>
                    <span className="text-gray-500 whitespace-nowrap">{fmtTime(a.created_at)}</span>
                  </div>
                  {a.last_error && (
                    <p className="mt-2 text-red-300 text-[11px] flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      {a.last_error}
                    </p>
                  )}
                  {a.retry_count > 0 && (
                    <p className="mt-1 text-gray-500 text-[10px]">Tentatives : {a.retry_count}</p>
                  )}
                  {a.status === 'done' && (
                    <button
                      onClick={async () => { await deleteOutboxAction(a.id); refreshList(); }}
                      className="mt-2 text-[10px] text-gray-500 hover:text-white underline"
                      data-testid={`offline-clear-${a.id}`}
                    >
                      Retirer de la liste
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OfflineStatusBar;
